import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterCategory, IndexedFilters, FilterRange } from '../types/filters';
import { debounce, isEmpty } from 'lodash-es';
import { isTypeNumberArray, isTypeNumberRange } from '../utils/filter-menu-type-guards';
import { SliderFilters } from '../components/side-panel-filters/filter-accordion/filter-option-slider/filter-option-slider';

// Helper util to format the slider value with the given prefix.
const formatSliderValue = (prefix: string, value: string | number) => `${prefix}${value}`;

// Predicate checking if the value is not a number, excluding the prefix if present.
export function isValueNaN(prefix: string, value: string) {
  return isNaN(Number(value.replaceAll(prefix, '')));
}

export interface FilterSliderProps {
  categoryName: FilterCategory;
  filters: SliderFilters;
  prefix: string;
  allowThumbOverlap?: boolean;
  step: number;
  selectedFilters: SliderFilters;
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
}

interface FilterSliderResponse {
  sliderMin: number;
  sliderMax: number;
  minValue: string;
  maxValue: string;
  leftThumbValue: string;
  rightThumbValue: string;
  value: number[];
  textInputMinError: boolean;
  textInputMaxError: boolean;
  handleMinChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMaxChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleMinBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  handleMaxBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  handleSliderChange: (values: number[]) => void;
  handleSliderCommitDebounced: (values: number[]) => void;
}

/**
 * Custom hook that provides handlers for sliders.
 */
export const useFilterSlider = ({
  categoryName,
  filters,
  prefix,
  allowThumbOverlap = true,
  step,
  selectedFilters,
  setSelectedFilters,
}: FilterSliderProps): FilterSliderResponse => {
  /**
   * STATE AND CONSTANTS
   */
  // Slider constants
  const sliderMin = isTypeNumberRange(filters) ? filters.min : 0;
  const sliderMax = isTypeNumberRange(filters) ? filters.max : filters.length - 1;

  // State for keeping current slider filters in sync with the parent selected filters
  const [currentSliderFilters, setCurrentSliderFilters] = useState(selectedFilters);
  const [currentParentFilters, setCurrentParentFilters] = useState(filters);

  // The index of current min and max values of the slider (only used when slider filters is an array of numbers)
  const [selectedMinIndex, setSelectedMinIndex] = useState(
    isTypeNumberArray(filters) && (selectedFilters as number[]).length > 0
      ? filters.indexOf(Math.min(...(selectedFilters as number[])))
      : sliderMin
  );
  const [selectedMaxIndex, setSelectedMaxIndex] = useState(
    isTypeNumberArray(filters) && (selectedFilters as number[]).length > 0
      ? filters.indexOf(Math.max(...(selectedFilters as number[])))
      : sliderMax
  );

  // The corresponding current min and max values of the slider (the numerical value)
  const [selectedMin, setSelectedMin] = useState(
    isTypeNumberArray(filters)
      ? filters[selectedMinIndex]
      : (selectedFilters as FilterRange<number>).min === -1
        ? filters.min
        : (selectedFilters as FilterRange<number>).min
  );
  const [selectedMax, setSelectedMax] = useState(
    isTypeNumberArray(filters)
      ? filters[selectedMaxIndex]
      : (selectedFilters as FilterRange<number>).max === -1
        ? filters.max
        : (selectedFilters as FilterRange<number>).max
  );

  // Slider value fed into the Radix UI slider
  const [value, setValue] = useState(
    isTypeNumberRange(filters)
      ? [selectedMin, selectedMax] // for number range sliders
      : [selectedMinIndex, selectedMaxIndex] // for number array sliders
  );

  // Text input errors
  const [textInputMinError, setTextInputMinError] = useState(false);
  const [textInputMaxError, setTextInputMaxError] = useState(false);

  // The text values of the min and max from the text inputs
  const [minValue, setMinValue] = useState(formatSliderValue(prefix, selectedMin));
  const [maxValue, setMaxValue] = useState(formatSliderValue(prefix, selectedMax));

  // The formatted values displayed under the left and right slider thumbs
  const leftThumbValue = useMemo(
    () => formatSliderValue(prefix, selectedMin),
    [prefix, selectedMin]
  );
  const rightThumbValue = useMemo(
    () => formatSliderValue(prefix, selectedMax),
    [prefix, selectedMax]
  );

  /**
   * HANDLER AND HELPER FUNCTIONS
   */

  // Using this assumes that the filters are an array of numbers.
  const setSelectedMinIndexWrapper = useCallback(
    (index: number) => {
      setSelectedMinIndex(index);
      setSelectedMin((filters as number[])[index]);
    },
    [filters]
  );

  // Using this assumes that the filters are an array of numbers
  const setSelectedMaxIndexWrapper = useCallback(
    (index: number) => {
      setSelectedMaxIndex(index);
      setSelectedMax((filters as number[])[index]);
    },
    [filters]
  );

  /**
   * On change, update the min value in the text input.
   *
   * @param e Change event of text input with the proposed new min value.
   */
  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMinValue(e.target.value);

  /**
   * On change, update the max value in the text input.
   *
   * @param e Change event of text input with the proposed new max value.
   */
  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setMaxValue(e.target.value);

  /**
   * Update the selected filters based on the new min and max indices. This is only for number array sliders.
   */
  const updateSelectedFiltersNumberArray = useCallback(
    (minIndex: number, maxIndex: number, filters: number[]) => {
      setSelectedFilters((prev) => {
        // If range of min and max indices consists of all filters, remove the filter.
        if (maxIndex - minIndex + 1 === filters.length) {
          const { [categoryName]: _, ...rest } = prev;
          return rest;
        }

        // Filter for the new selected filters based on the new min/max values.
        const updatedValues = filters.filter((_, i) => minIndex <= i && i <= maxIndex);

        // Update/create the filter value list.
        return { ...prev, [categoryName]: updatedValues };
      });
    },
    [categoryName, setSelectedFilters]
  );

  /**
   * Update the selected filters based on the new min and max values. This is only for number range sliders.
   */
  const updateSelectedFiltersNumberRange = useCallback(
    (min: number, max: number) => {
      setSelectedFilters((prev) => {
        // If the committed min and max values are the same as the slider range's min and max values, remove the filter.
        if (min === sliderMin && max === sliderMax) {
          const { [categoryName]: _, ...rest } = prev;
          return rest;
        }

        // Update/create the filter value list.
        return { ...prev, [categoryName]: { min, max } };
      });
    },
    [categoryName, setSelectedFilters, sliderMax, sliderMin]
  );

  /**
   * On slider commit, update the selected filters based on the given min/max values. If the range of min and max
   * indices consists of all filters, remove the filter.
   *
   * @param min The index of the minimum value in the slider if number array slider, otherwise is numerical min value.
   * @param max The index of the maximum value in the slider if number array slider, otherwise is numerical max value.
   */
  const handleSliderCommit = useCallback(
    (min: number, max: number) => {
      if (isTypeNumberArray(filters)) {
        updateSelectedFiltersNumberArray(min, max, filters);
      } else {
        updateSelectedFiltersNumberRange(min, max);
      }
    },
    [filters, updateSelectedFiltersNumberArray, updateSelectedFiltersNumberRange]
  );

  /**
   * Calculate the next value (or index) based on the new value in the text input.
   *
   * @param nextValue The parsed new value from the text input.
   * @param forMin Whether the value is for the min or max index. True for min, false for max.
   */
  const calculateNextValue = useCallback(
    (nextValue: number, forMin: boolean) => {
      /**
       * If the slider allows thumb overlap, the adjustment is 0. Otherwise, the adjustment is the slider step value.
       */
      const overlapAdjustment = allowThumbOverlap ? 0 : step;
      if (forMin) {
        /**
         * Logic for new current min:
         * - If blurred value is less than numerical value of slider min, set min value to slider min.
         * - If blurred value is greater or equal to the current max value, set new selected min to the current selected max (or current selected max index) so that the thumbs overlap.
         *   - If the slider does not allow thumb overlap, the new selected min is adjusted by subtracting the slider step.
         * - Otherwise, set the min value to the blurred value (or index of blurred value).
         */
        if (nextValue < (isTypeNumberArray(filters) ? filters[sliderMin] : sliderMin)) {
          return sliderMin;
        } else if (nextValue >= selectedMax) {
          return isTypeNumberArray(filters)
            ? selectedMaxIndex - overlapAdjustment
            : selectedMax - overlapAdjustment;
        } else {
          return isTypeNumberArray(filters) ? filters.indexOf(nextValue) : nextValue;
        }
      } else {
        /**
         * Logic for new current max:
         * - If blurred value is greater than numerical value of slider max, set max value to slider max.
         * - If blurred value is less than or equal to the current min value, set new selected max to the current selected min (or current selected min index) so that the thumbs overlap.
         *   - If the slider does not allow thumb overlap, the new selected max is adjusted by adding the slider step.
         * - Otherwise, set the max value to the blurred value (or index of blurred value).
         */
        if (nextValue > (isTypeNumberArray(filters) ? filters[sliderMax] : sliderMax)) {
          return sliderMax;
        } else if (nextValue <= selectedMin) {
          return isTypeNumberArray(filters)
            ? selectedMinIndex + overlapAdjustment
            : selectedMin + overlapAdjustment;
        } else {
          return isTypeNumberArray(filters) ? filters.indexOf(nextValue) : nextValue;
        }
      }
    },
    [
      allowThumbOverlap,
      filters,
      selectedMax,
      selectedMaxIndex,
      selectedMin,
      selectedMinIndex,
      sliderMax,
      sliderMin,
      step,
    ]
  );

  /**
   * General blur handler for min and max text inputs. This function is called whenever the text input loses focus.
   * The logic it follows to update the min and max follows depends on the type of slider filters being used (number array or number range)
   * and whether the blur event is for the min or max text input (indicated by the forMin flag).
   */
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>, forMin: boolean) => {
      const handleUpdateForBlur = (prev: number) => {
        const { value } = e.target;

        // Don't update min index if the value is empty, the prefix, or not a number.
        if (value === '' || value === prefix || isValueNaN(prefix, value)) {
          return prev;
        }

        // Calculate next valid value based on the new value from the text input.
        const parsedValue = Number(prefix ? value.replaceAll(prefix, '') : value);
        const nextValue = calculateNextValue(parsedValue, forMin);

        // Commit the new min/max values based on the new value from the text input.
        if (forMin) {
          handleSliderCommit(
            nextValue,
            isTypeNumberArray(filters) ? selectedMaxIndex : selectedMax
          );
        } else {
          handleSliderCommit(
            isTypeNumberArray(filters) ? selectedMinIndex : selectedMin,
            nextValue
          );
        }

        // Keep selected min/max in sync if filters is number array.
        if (isTypeNumberArray(filters)) {
          if (forMin) {
            setSelectedMin(filters[nextValue]);
          } else {
            setSelectedMax(filters[nextValue]);
          }
        }

        return nextValue;
      };

      if (isTypeNumberArray(filters)) {
        if (forMin) {
          setSelectedMinIndex(handleUpdateForBlur);
        } else {
          setSelectedMaxIndex(handleUpdateForBlur);
        }
      } else {
        if (forMin) {
          setSelectedMin(handleUpdateForBlur);
        } else {
          setSelectedMax(handleUpdateForBlur);
        }
      }
    },
    [
      calculateNextValue,
      filters,
      handleSliderCommit,
      prefix,
      selectedMax,
      selectedMaxIndex,
      selectedMin,
      selectedMinIndex,
    ]
  );

  /**
   * On blur, update the selected min index based on the new min value in the text input.
   *
   * @param e Blur event of text input with the proposed new min value.
   */
  const handleMinBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    handleBlur(e, true);
  };

  /**
   * On blur, update the selected max index based on the new max value in the text input.
   *
   * @param e Blur event of text input with the proposed new max value.
   */
  const handleMaxBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    handleBlur(e, false);
  };

  /**
   * Handle the slider change event. This function is called whenever the slider thumbs move.
   *
   * NOTE: This function does not (usually) commit the slider values to the selected filters! It is intended to update the state used for displaying the thumb and text input values.
   */
  const handleSliderChange = useCallback(
    (values: number[]) => {
      if (isTypeNumberArray(filters)) {
        // To plug a hole in the Radix UI's slider not triggering the onValueCommit callback when the right thumb overlaps
        // the left thumb (i.e. when the right thumb becomes the same value as the left thumb), we need to manually trigger
        // our slider commit callback from the onChange event since this event does trigger when the right thumb overlaps the left thumb.
        if (
          values[0] === values[1] &&
          values[0] === selectedMinIndex &&
          values[1] !== selectedMaxIndex
        ) {
          handleSliderCommit(values[0], values[1]);
        }

        // The slider values given will always be in ascending order.
        setSelectedMinIndexWrapper(values[0]);
        setSelectedMaxIndexWrapper(values[1]);
      } else {
        // To plug a hole in the Radix UI's slider not triggering the onValueCommit callback when the right thumb overlaps
        // the left thumb (i.e. when the right thumb becomes the same value as the left thumb), we need to manually trigger
        // our slider commit callback from the onChange event since this event does trigger when the right thumb overlaps the left thumb.
        if (
          values[0] === values[1] &&
          values[0] === selectedMin &&
          values[1] !== selectedMax
        ) {
          handleSliderCommit(values[0], values[1]);
        }

        // The slider values given will always be in ascending order.
        setSelectedMin(values[0]);
        setSelectedMax(values[1]);
      }
    },
    [
      filters,
      handleSliderCommit,
      selectedMax,
      selectedMaxIndex,
      selectedMin,
      selectedMinIndex,
      setSelectedMaxIndexWrapper,
      setSelectedMinIndexWrapper,
    ]
  );

  // Debounce the slider commit function as a safety measure in case the slider commit is spammed. This is unlikely to happen
  // since this callback only triggers when the slider thumbs stop moving and are released, but it's nice to have here just in case.
  const handleSliderCommitDebounced = debounce((values: number[]) => {
    handleSliderCommit(values[0], values[1]);
  }, 150);

  /**
   * Sync the text input values as the parent filter options change.
   */
  const syncTextInputsOnParentFilterChange = useCallback(
    (filters: SliderFilters) => {
      const syncTextInputs = (parentFilterMin: number, parentFilterMax: number) => {
        setMinValue(formatSliderValue(prefix, Math.max(selectedMin, parentFilterMin)));
        setMaxValue(formatSliderValue(prefix, Math.min(selectedMax, parentFilterMax)));
      };

      if (isTypeNumberArray(filters)) {
        syncTextInputs(filters[0], filters[filters.length - 1]);
      } else {
        syncTextInputs(filters.min, filters.max);
      }
    },
    [prefix, selectedMax, selectedMin]
  );

  /**
   * EFFECTS
   */
  // Keep the text input values in sync when the slider thumbs move.
  useEffect(() => {
    setMinValue(formatSliderValue(prefix, selectedMin));
    setMaxValue(formatSliderValue(prefix, selectedMax));
  }, [prefix, selectedMin, selectedMax]);

  // Update text error message display as the text input values change.
  useEffect(() => {
    setTextInputMinError(isValueNaN(prefix, minValue));
    setTextInputMaxError(isValueNaN(prefix, maxValue));
  }, [prefix, minValue, maxValue]);

  // Keep the slider value in sync with the selectedMinIndex/selectedMaxIndex or selectedMin/selectedMax.
  useEffect(() => {
    setValue(
      isTypeNumberRange(filters)
        ? [selectedMin, selectedMax] // for number range sliders
        : [selectedMinIndex, selectedMaxIndex] // for number array sliders
    );
  }, [filters, selectedMax, selectedMaxIndex, selectedMin, selectedMinIndex]);

  useEffect(() => {
    /** If parent selected filter state does not equal slider selected filter state, sync them */
    if (isTypeNumberArray(currentSliderFilters)) {
      if (
        currentSliderFilters.length !== (selectedFilters as number[]).length ||
        !currentSliderFilters.every((v) => (selectedFilters as number[]).includes(v))
      ) {
        setCurrentSliderFilters(selectedFilters);
      }
    } else {
      if (
        currentSliderFilters.min !== (selectedFilters as FilterRange<number>).min ||
        currentSliderFilters.max !== (selectedFilters as FilterRange<number>).max
      ) {
        setCurrentSliderFilters(selectedFilters);
      }
    }
  }, [currentSliderFilters, selectedFilters]);

  useEffect(() => {
    /** If parent filter options state does not equal slider filter options state, sync them */
    if (isTypeNumberArray(currentParentFilters)) {
      if (
        currentParentFilters.length !== (filters as number[]).length ||
        !currentParentFilters.every((v) => (filters as number[]).includes(v))
      ) {
        setCurrentParentFilters(filters);

        // Sync the selected min and max index with the new filter options if necessary
        syncTextInputsOnParentFilterChange(filters);
      }
    } else {
      if (
        currentParentFilters.min !== (filters as FilterRange<number>).min ||
        currentParentFilters.max !== (filters as FilterRange<number>).max
      ) {
        setCurrentParentFilters(filters);

        // Sync the selected min and max with the new filter options if necessary
        syncTextInputsOnParentFilterChange(filters);
      }
    }
  }, [currentParentFilters, filters, syncTextInputsOnParentFilterChange]);

  useEffect(() => {
    /** If parent selected filter state is empty but local state is not, this indicates a reset of the slider to min and max filtering
     * We do this as a mechanism of recognizing when the parent filtering has been clear for a slider and the local slider state is out of sync.
     */
    if (isTypeNumberArray(selectedFilters)) {
      if (isEmpty(selectedFilters) && !isEmpty(currentSliderFilters)) {
        setSelectedMinIndexWrapper(sliderMin);
        setSelectedMaxIndexWrapper(sliderMax);
      }
    } else {
      if (
        selectedFilters.min === -1 &&
        selectedFilters.max === -1 &&
        (currentSliderFilters as FilterRange<number>).min !== -1 &&
        (currentSliderFilters as FilterRange<number>).max !== -1
      ) {
        setSelectedMin(sliderMin);
        setSelectedMax(sliderMax);
      }
    }
  }, [
    selectedFilters,
    currentSliderFilters,
    sliderMin,
    sliderMax,
    setSelectedMinIndexWrapper,
    setSelectedMaxIndexWrapper,
  ]);

  useEffect(() => {
    /** If parent filter options change the min and max range of the slider while the slider has no selected filter, update the slider thumbs and state
     * to the new min and max to retain that same unselected state.
     */
    if (isTypeNumberArray(currentParentFilters)) {
      // No selected filter case
      if (isEmpty(selectedFilters)) {
        setSelectedMinIndexWrapper(0);
        setSelectedMaxIndexWrapper(currentParentFilters.length - 1);
      }
    } else {
      // No selected filter case
      if (
        (selectedFilters as FilterRange<number>).min === -1 &&
        (selectedFilters as FilterRange<number>).max === -1
      ) {
        setSelectedMin(currentParentFilters.min);
        setSelectedMax(currentParentFilters.max);
      }
    }
  }, [
    currentParentFilters,
    selectedFilters,
    setSelectedMaxIndexWrapper,
    setSelectedMinIndexWrapper,
  ]);

  return {
    sliderMin,
    sliderMax,
    minValue,
    maxValue,
    leftThumbValue,
    rightThumbValue,
    value,
    textInputMinError,
    textInputMaxError,
    handleMinChange,
    handleMaxChange,
    handleMinBlur,
    handleMaxBlur,
    handleSliderChange,
    handleSliderCommitDebounced,
  };
};
